import {
  chartColors,
  DashboardTimeZone,
  KpiTile,
  LabeledPieChart,
  metricOf,
  metricValue,
  otherColor,
  useAnalyticsBreakdown,
  useAnalyticsOverTime,
  useAnalyticsTotals,
  viewerFilters,
  WidgetError,
  type AnalyticsQuestion,
  type DashboardZone,
} from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  RollingDatePreset,
  type ViewDateValue,
} from 'corso-types';
import { formatter } from 'corso-web-lib';
import { match } from 'ts-pattern';
import { storeSource } from '~/features/analytics/analyticsSources';
import { useProduct } from '~/features/products/ProductProvider';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import useIsAdmin from '~/hooks/useIsAdmin';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';

export const productAnalyticsDate = {
  kind: 'preset',
  preset: RollingDatePreset.last90Days,
} satisfies ViewDateValue;

// a title the analytics filter can't take, such as an empty or very long one, has nothing to ask for
export const isAnalyzableTitle = (title: string) =>
  viewerFilters.shape.productTitles.safeParse([title]).success;

const topReasons = 3;

function ProductAnalyticsSection({ title }: { title: string }) {
  const { store } = useStoreUser();
  const enabled = useEnabledClaimType();
  const timeZone: DashboardZone = store.ianaTimezone
    ? { key: DashboardTimeZone.store, iana: store.ianaTimezone }
    : { key: DashboardTimeZone.local };

  const source = storeSource(useStoreId(), store.currencyCode);
  // the line tables carry no product id, so the product is matched on its title
  const productTitles = [title];

  const sold = useAnalyticsOverTime({
    scope: {
      source,
      dataset: 'orderItems',
      date: productAnalyticsDate,
      timeZone,
      timeField: 'createdOn',
      filters: { productTitles },
    },
    metrics: ['itemsSold', 'returnRate', 'warrantyRate'],
    dimensions: [],
  } satisfies AnalyticsQuestion<'orderItems'>);

  // the return rate alone is read against the store's, which tells whether it is high
  const storeReturns = useAnalyticsTotals(
    enabled.isReturnsEnabled
      ? ({
          scope: {
            source,
            dataset: 'orderItems',
            date: productAnalyticsDate,
            timeZone,
            timeField: 'createdOn',
            filters: {},
          },
          metrics: ['returnRate'],
          dimensions: [],
        } satisfies AnalyticsQuestion<'orderItems'>)
      : undefined,
  );

  const reasons = useAnalyticsBreakdown(
    {
      scope: {
        source,
        dataset: 'claimItems',
        date: productAnalyticsDate,
        timeZone,
        timeField: 'createdOn',
        filters: { productTitles, claimTypes: ['Return'] },
      },
      // every reason, so the top ones are read as a share of all returned items
      metrics: ['items'],
      dimensions: ['reason'],
    } satisfies AnalyticsQuestion<'claimItems'>,
    enabled.isReturnsEnabled,
  );

  const refunds = useAnalyticsTotals(
    enabled.isReturnsEnabled
      ? ({
          scope: {
            source,
            dataset: 'claimItems',
            date: productAnalyticsDate,
            timeZone,
            timeField: 'createdOn',
            filters: {
              productTitles,
              claimTypes: ['Return'],
              resolutions: [CrewClaimResolutionMethodEnum.refund],
            },
          },
          metrics: ['resolvedAmount'],
          dimensions: [],
        } satisfies AnalyticsQuestion<'claimItems'>)
      : undefined,
  );

  const registrationsAsked = {
    scope: {
      source,
      dataset: 'registrations',
      date: productAnalyticsDate,
      timeZone,
      timeField: 'createdOn',
      filters: { productTitles },
    },
    metrics: ['registrations'],
    dimensions: [],
  } satisfies AnalyticsQuestion<'registrations'>;
  const registered = useAnalyticsOverTime(
    registrationsAsked,
    registrationsAsked,
    enabled.isRegistrationEnabled,
  );

  if (
    enabled.isError ||
    sold.status === 'error' ||
    registered.status === 'error' ||
    reasons.isError ||
    refunds.isError
  ) {
    return <WidgetError title="product analytics" />;
  }

  // a program that is off asks nothing, so it is never waited on; the store averages are only a description
  if (
    !enabled.hasLoaded ||
    sold.status === 'pending' ||
    (enabled.isRegistrationEnabled && registered.status === 'pending') ||
    (enabled.isReturnsEnabled && (reasons.isPending || refunds.isPending))
  ) {
    return null;
  }

  const { totals, points } = sold.data;

  const itemsSold = metricValue('orderItems', 'itemsSold', totals) ?? 0;
  const returnRate = metricValue('orderItems', 'returnRate', totals);
  const warrantyRate = metricValue('orderItems', 'warrantyRate', totals);
  const refunded =
    metricValue('claimItems', 'resolvedAmount', refunds.data) ?? 0;
  const registrationPoints = match(registered)
    .with({ status: 'success' }, ({ data }) => data)
    .otherwise(() => undefined);
  const registrations =
    metricValue('registrations', 'registrations', registrationPoints?.totals) ??
    0;
  const reasonRows = reasons.data?.rows ?? [];
  const returnedItems = reasonRows.reduce(
    (sum, { row }) => sum + (metricValue('claimItems', 'items', row) ?? 0),
    0,
  );
  const topReasonSlices = reasonRows
    .flatMap(({ key, groups: [group = null], row }) =>
      group === null || group === ''
        ? []
        : [
            {
              key,
              label: group,
              value: metricValue('claimItems', 'items', row) ?? 0,
            },
          ],
    )
    .slice(0, topReasons)
    .map((slice, index) => ({
      ...slice,
      color: chartColors[index % chartColors.length] ?? otherColor,
    }));
  // the rest, unset reasons included, so the slices add up to every returned item
  const otherItems =
    returnedItems - topReasonSlices.reduce((sum, { value }) => sum + value, 0);
  const reasonSlices =
    otherItems > 0
      ? [
          ...topReasonSlices,
          {
            key: 'other',
            label: 'Other',
            value: otherItems,
            color: otherColor,
          },
        ]
      : topReasonSlices;

  const showsItemsSold = itemsSold > 0;
  const showsReturnRate = enabled.isReturnsEnabled && returnRate !== undefined;
  const showsWarrantyRate =
    enabled.isWarrantyEnabled && warrantyRate !== undefined;
  const showsRefunded = enabled.isReturnsEnabled && refunded > 0;
  const showsReasons = enabled.isReturnsEnabled && topReasonSlices.length > 0;

  const showsRegistrations = enabled.isRegistrationEnabled && registrations > 0;

  if (
    !showsItemsSold &&
    !showsRefunded &&
    !showsRegistrations &&
    !showsReasons
  ) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-bold text-neutral-700">
        Analytics (last 90 days)
      </h2>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="grid flex-1 grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] content-start gap-4">
          {showsItemsSold && (
            <KpiTile
              label={metricOf('orderItems', 'itemsSold').label}
              value={formatter.count(itemsSold)}
              trend={points.map(
                ({ row }) =>
                  metricValue('orderItems', 'itemsSold', row) ?? null,
              )}
            />
          )}
          {showsReturnRate && (
            <KpiTile
              label={metricOf('orderItems', 'returnRate').label}
              value={formatter.percent(returnRate)}
              description={match(
                metricValue('orderItems', 'returnRate', storeReturns.data),
              )
                .with(undefined, () => undefined)
                .otherwise(
                  (rate) => `Store average ${formatter.percent(rate)}`,
                )}
              info={metricOf('orderItems', 'returnRate').info}
            />
          )}
          {showsWarrantyRate && (
            <KpiTile
              label={metricOf('orderItems', 'warrantyRate').label}
              value={formatter.percent(warrantyRate)}
              info={metricOf('orderItems', 'warrantyRate').info}
            />
          )}
          {showsRefunded && (
            <KpiTile
              label="Refunded"
              value={formatter.currency(refunded, store.currencyCode)}
              info="Refunded amounts on return requests opened in the range."
            />
          )}
          {showsRegistrations && (
            <KpiTile
              label={metricOf('registrations', 'registrations').label}
              value={formatter.count(registrations)}
              trend={registrationPoints?.points.map(
                ({ row }) =>
                  metricValue('registrations', 'registrations', row) ?? null,
              )}
            />
          )}
        </div>
        {showsReasons && (
          <div className="lg:w-80 lg:shrink-0">
            <LabeledPieChart title="Return reasons" data={reasonSlices} />
          </div>
        )}
      </div>
    </section>
  );
}

// the dashboards are for admins alone, so their numbers are too
export function ProductAnalytics() {
  const { title } = useProduct();
  const isAdmin = useIsAdmin();

  if (!isAdmin || !isAnalyzableTitle(title)) return null;

  return <ProductAnalyticsSection title={title} />;
}
